/**
 * Auditing Workspace Tokens
 * Values from the auditing refinement handoff (design_handoff_auditing_refinement/README.md §4).
 * Scoped to the auditing workspace; shared panels read them only through their `variant="auditing"` path.
 */

const SETTLE_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const EXIT_EASING = 'cubic-bezier(0.4, 0, 1, 1)';

/** The chat dock is brand blue in BOTH themes (it is a brand surface, like the side rail). */
const CHAT = {
  background: '#1a63a8', // 2026-10-12: was #1f6db4; one step deeper so white at .9-.94 alpha clears 4.5:1 over the input fill/bubble
  divider: 'rgba(255,255,255,.12)',
  composerDivider: 'rgba(255,255,255,.14)',
  // 2026-10-12 contrast sweep: bubble .16 -> .10 (white ink was 3.95:1 on it), subtitle .72 -> .9 (3.6:1),
  // placeholder .78 -> .94 (3.47:1 over the input fill), input edge .3 -> .6 (1.77:1; a control boundary needs 3:1).
  bennieBubble: 'rgba(255,255,255,.10)',
  subtitle: 'rgba(255,255,255,.9)',
  placeholder: 'rgba(255,255,255,.94)',
  chipBorder: 'rgba(255,255,255,.35)',
  inputBorder: 'rgba(255,255,255,.7)',
  inputFill: 'rgba(255,255,255,.08)',
  handleFill: 'rgba(255,255,255,.92)',
  ink: '#fff',
  iconTile: '#fff',
  collapseIcon: 'rgba(255,255,255,.8)',
  userBubble: '#fff',
  userInk: '#002855',
  sendFill: '#ffc627',
  sendInk: '#002855',
  // A disabled Send used to be the gold fill at .45 opacity, which composites to olive with a muddy ink.
  // A pale brand tint with the full-strength ink reads as "not available" without losing the label.
  // Citation dagger inside a bubble: pale yellow on the assistant bubble (blue on blue was invisible), deep blue on white.
  bennieCite: '#fff6cc',
  userCite: '#00467f',
  sendDisabledFill: '#a9c4e0',
  sendDisabledInk: '#002855',
} as const;

/**
 * The auditing workspace palette, per theme. Every leaf is exposed through a CSS custom property (see
 * `auditingColors` and `auditingCssVars`), so the ~80 call sites keep reading `auditingColors.x.y` and the
 * theme switch happens in the cascade. The light column is the handoff's own values, except where the
 * 2026-10-12 contrast sweep measured a failing pair (noted inline).
 */
export const auditingPalette = {
  light: {
    ink: '#101828',
    body: '#344054',
    bodySoft: '#475467',
    muted: '#667085',
    faint: '#667085', // was #8a919c, 3.18:1 on a white card
    faint2: '#5f6a7c', // was #98a2b3, 2.58:1 (4.5:1 needed on pillSelected too)
    rule: '#e6e8ec',
    versionRule: '#ededed',
    ruleSoft: '#eef0f3',
    ruleSofter: '#f1f2f4',
    rowHover: '#f8f9fb',
    pillSelected: '#f2f4f7',
    link: '#00467f',
    linkHover: '#00305a',
    linkUnderline: '#b6c6d8',
    shadowInk: 'rgba(16,40,80,.05)',
    tone: {
      effective: '#1f7a4d',
      caution: '#8f5a0b', // was #b7791f, 3.64:1
      negative: '#b42318',
      neutral: '#667085',
      low: '#2b6cb0',
    },
    panel: {
      fill: '#f3f6fa',
      ring: '#e6ecf4',
      ink: '#14243a',
      body: '#46546a',
      caption: '#586579', // was #7a8799, 3.37:1 on the panel fill
      pillFill: '#e3eefb',
      pillInk: '#0a4f94',
      card: '#ffffff', // an item card / table row resting on `fill` (was the literal `common.white`)
    },
    planItem: { fill: 'rgba(255,255,255,.7)' },
    scrollLine: { active: '#5b94c9', near: '#aac4de', far: '#d9dfe7' },
  },
  // Dark counterparts. The page is #1a1a1a, a card #2d2d2d; the tonal panel sits between them and the
  // rows/cards inside it step up one level, so the nesting still reads (panel < card) without a light surface.
  dark: {
    ink: '#f2f4f7',
    body: '#d0d5dd',
    bodySoft: '#b9c0cc',
    muted: '#a9b1bf',
    faint: '#b0b7c3',
    faint2: '#a9b0bd',
    rule: '#3d424b',
    versionRule: '#404040',
    ruleSoft: '#353a42',
    ruleSofter: '#30343b',
    rowHover: '#3a3f48',
    pillSelected: '#3d424b',
    link: '#7dbbff',
    linkHover: '#a9d1f7',
    linkUnderline: '#4a6a8a',
    shadowInk: 'rgba(0,0,0,.35)',
    tone: {
      effective: '#5fcf9a',
      caution: '#e6b050',
      negative: '#f4948c',
      neutral: '#a9b1bf',
      low: '#82b6f0',
    },
    panel: {
      fill: '#23272e',
      ring: '#363b44',
      ink: '#f2f4f7',
      body: '#c9d0db',
      caption: '#a9b1bf',
      pillFill: '#1f3a5c',
      pillInk: '#b3d5fa',
      card: '#32363e',
    },
    planItem: { fill: 'rgba(255,255,255,.05)' },
    scrollLine: { active: '#5b94c9', near: '#6f87a1', far: '#4a5160' },
  },
} as const;

type Widen<T> = T extends string ? string : { -readonly [K in keyof T]: Widen<T[K]> };
type ThemedPalette = Widen<(typeof auditingPalette)['light']>;

const VAR_PREFIX = '--aud';

function walk(node: unknown, path: string[], visit: (path: string[], leaf: string) => void): void {
  if (typeof node === 'string') visit(path, node);
  else for (const [k, v] of Object.entries(node as Record<string, unknown>)) walk(v, [...path, k], visit);
}

/** `--aud-panel-fill` for ['panel', 'fill']. */
const varName = (path: string[]) => `${VAR_PREFIX}-${path.join('-')}`;

function asVars(node: unknown, path: string[] = []): unknown {
  if (typeof node === 'string') return `var(${varName(path)}, ${node})`;
  return Object.fromEntries(Object.entries(node as Record<string, unknown>).map(([k, v]) => [k, asVars(v, [...path, k])]));
}

/** The palette as `var(--aud-…, <light value>)` references: the shape of either theme's column. */
export const auditingColors = {
  ...(asVars(auditingPalette.light) as ThemedPalette),
  chat: CHAT,
} as ThemedPalette & { chat: typeof CHAT };

/** The custom properties for one theme, mounted on `:root` by the global styles. */
export function auditingCssVars(mode: 'light' | 'dark'): Record<string, string> {
  const out: Record<string, string> = {};
  walk(auditingPalette[mode], [], (path, leaf) => {
    out[varName(path)] = leaf;
  });
  return out;
}

export const auditingShadows = {
  panelCard: `0 0 0 1px ${auditingColors.panel.ring}, 0 1px 3px ${auditingColors.shadowInk}`,
  panelRow: `0 0 0 1px ${auditingColors.panel.ring}`,
  handle: '0 1px 4px rgba(0,0,0,.06)',
} as const;

export const auditingMotion = {
  settleEasing: SETTLE_EASING,
  exitEasing: EXIT_EASING,
  underline: `left .24s ${SETTLE_EASING}, width .24s ${SETTLE_EASING}`,
  chatOpenMs: 260,
  chatCloseMs: 200,
  chatIn: `auditChatIn .26s ${SETTLE_EASING} both`,
  chatOut: `auditChatOut .2s ${EXIT_EASING} both`,
  handleIn: `auditHandleIn .24s ${SETTLE_EASING} both`,
  tabIn: 'auditTabIn .2s cubic-bezier(.2,.8,.2,1) both',
  expandIn: 'auditTabIn .2s ease both',
  sectionIn: 'auditTabIn .25s ease both',
} as const;

/**
 * Chat panel width: `--audit-chat-width` = clamp(minWidth, 34vw, maxWidth); 100% below fullWidthBelowPx.
 * The panel starts closed below startClosedBelowPx (operator ruling 2026-10-07: responsive, no overlay).
 */
export const auditingChat = {
  maxWidth: 400,
  minWidth: 300,
  startClosedBelowPx: 1100,
  fullWidthBelowPx: 600,
} as const;

/**
 * Design / Operating effective tonal panels (handoff §8). Plain style objects, spread into `sx`.
 * `section` is every block; `card` an item or status card; `gridHeader` / `gridRow` the Files and
 * Run record tables, whose column templates are `gridColumns`.
 */
export const auditingPanel = {
  stackGap: '20px',
  section: {
    padding: '20px',
    bgcolor: auditingColors.panel.fill,
    borderRadius: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '2px 6px 16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '-.15px',
    color: auditingColors.panel.ink,
  },
  bodyText: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: auditingColors.panel.body,
  },
  card: {
    padding: '18px 20px',
    marginBottom: '10px',
    bgcolor: auditingColors.panel.card,
    borderRadius: '16px',
    boxShadow: auditingShadows.panelCard,
  },
  numberPill: {
    fontSize: '11.5px',
    fontWeight: 700,
    color: auditingColors.panel.pillInk,
    bgcolor: auditingColors.panel.pillFill,
    borderRadius: '999px',
    padding: '0 9px',
    lineHeight: '22px',
    flex: 'none',
  },
  itemTitle: {
    fontSize: '14.5px',
    fontWeight: 600,
    color: auditingColors.panel.ink,
  },
  gridHeader: {
    display: 'grid',
    gap: '16px',
    padding: '0 18px 10px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '.7px',
    textTransform: 'uppercase',
    color: auditingColors.panel.caption,
  },
  gridRow: {
    display: 'grid',
    gap: '16px',
    padding: '16px 18px',
    marginBottom: '8px',
    bgcolor: auditingColors.panel.card,
    borderRadius: '14px',
    boxShadow: auditingShadows.panelRow,
    alignItems: 'center',
    fontSize: '14px',
    lineHeight: 1.5,
    color: auditingColors.panel.ink,
  },
  gridColumns: {
    files: '2.2fr 3fr 1.2fr 1.7fr',
    runs: '1.2fr 1.4fr 1fr .7fr 3.2fr .7fr',
  },
} as const;
